import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { CreditCard, TrendingUp, TrendingDown, DollarSign, Users, ChevronRight, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUsd = (n) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * OwnerAdminPaymentsCard — platform-wide Stripe Connect revenue snapshot.
 *
 * Visible ONLY in the Owner Admin dashboard (separate-login, owner-only).
 * Shows MTD platform fees, gross volume processed, active subscribers,
 * MoM growth, and the top 5 earning workspaces this month.
 */
export default function OwnerAdminPaymentsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Owner Admin uses session-cookie auth; standard fetch with credentials works.
        const r = await fetch(`${API_URL}/api/payment-stats/platform`, {
          credentials: 'include',
        });
        if (r.ok) setData(await r.json());
      } catch (e) {
        console.error('Failed to load platform payment stats', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-card border-white/15">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-400" />
            Platform Payment Revenue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-12 w-1/3 bg-white/5" />
          <Skeleton className="h-4 w-1/2 bg-white/5" />
          <Skeleton className="h-32 w-full bg-white/5" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const mtd = data.month_to_date || {};
  const allTime = data.all_time || {};
  const delta = data.change_pct_vs_prior_month;
  const isUp = delta != null && delta >= 0;
  const top = data.top_workspaces_this_month || [];

  return (
    <Card className="bg-card border-white/15" data-testid="owner-admin-payments-card">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-400" />
              Platform Payment Revenue
            </CardTitle>
            <CardDescription className="text-muted-foreground/70 mt-1">
              CountBucks platform fees collected via Stripe Connect this month
            </CardDescription>
          </div>
          {delta != null && (
            <Badge
              className={isUp
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                : 'bg-red-500/20 text-red-300 border-red-500/30'}
            >
              {isUp ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {isUp ? '+' : ''}{delta}% vs last month
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Big numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">MTD Platform Fees</p>
            <p className="text-3xl font-bold text-emerald-400">{fmtUsd(mtd.platform_fees_usd)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">MTD Gross Volume</p>
            <p className="text-2xl font-semibold text-white">{fmtUsd(mtd.gross_volume_usd)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Active Subscribers</p>
            <p className="text-2xl font-semibold text-white flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              {mtd.active_subscribers || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Payments This Month</p>
            <p className="text-2xl font-semibold text-white">{mtd.payment_count || 0}</p>
          </div>
        </div>

        {/* All-time band */}
        <div className="rounded-lg bg-white/5 border border-white/5 p-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">All-time platform fees collected</span>
          <span className="text-white font-semibold">{fmtUsd(allTime.platform_fees_usd)}</span>
        </div>

        {/* Top earners */}
        {top.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground">
                Top Earners This Month
              </h4>
              <Link
                to="/owner-admin/payments"
                className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                data-testid="payments-view-all-link"
              >
                View all payments <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="rounded-lg border border-white/5 divide-y divide-white/5">
              {top.map((tw, i) => (
                <Link
                  key={tw.workspace_id}
                  to={`/owner-admin/payments?workspace_id=${encodeURIComponent(tw.workspace_id)}`}
                  className="flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 transition-colors group"
                  data-testid={`top-workspace-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-muted-foreground text-xs w-5">#{i + 1}</span>
                    <span className="text-white truncate group-hover:text-emerald-300">{tw.workspace_name}</span>
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                      · {tw.payment_count} payment{tw.payment_count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <div className="text-right">
                      <div className="text-emerald-400 font-semibold">{fmtUsd(tw.platform_fees_usd)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtUsd(tw.gross_volume_usd)} gross</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60 group-hover:text-emerald-400" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {top.length === 0 && (
          <div className="text-center py-4 text-muted-foreground text-sm">
            <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>No platform-fee revenue yet this month.</p>
            <p className="text-xs mt-1">
              Subscribers must enable a platform fee in <span className="text-white">Settings → Payments</span> for fees to land here.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
